/**
 * Who inside a customer is spending the money (redesign phase 3, item B1/B2).
 *
 * ── The gap this closes ─────────────────────────────────────────────────
 *
 * The tenant TOTAL was the only figure the platform console had. But the
 * actionable questions all live one level down — is one user consuming the
 * whole org's allowance, is a team cap mis-set, why was this person blocked —
 * and `GET /api/orgs/:id/usage` answers them behind a MEMBERSHIP gate, so a
 * platform admin who is not in the customer's org got a 403.
 *
 * ── Why the arithmetic is rendered, not just the result ─────────────────
 *
 * A member's ceiling is `additive(roleCap, teamSum, adjust)`. Showing only the
 * total leaves "why was this user blocked" unanswerable — and the unknown-role
 * rule makes that sharp: a membership carrying a role the code does not
 * recognise silently caps that user at ZERO, and nothing anywhere would say so.
 *
 * `resolveCaps` already returns `components` for exactly this purpose, so this
 * is a render rather than a computation — and it is the same function the 402
 * decision uses, so what is shown here cannot drift from what is enforced.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useBillingCurrency } from '@papan/core-ui/ui'
import { formatMoney, CAP_ROLES } from '@papan/types'
import {
  budgetApi, type OrgUsageResponse, type MemberUsageRow, type PendingInvite,
} from '../../client/budget'
import { Section } from './bits'

export function CustomerPeople({ tenantId, canEdit = false }: {
  tenantId: string
  /** `customers:write`. Gates the invite actions only — the spend figures on
   *  this tab are readable by anyone who can open the customer. */
  canEdit?: boolean
}) {
  const { t } = useTranslation('core-ui')
  const moneyOpts = { currency: useBillingCurrency() }
  const [data, setData] = useState<OrgUsageResponse | null>(null)
  const [invites, setInvites] = useState<PendingInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Which add-people form is showing. One is ALWAYS visible below the button
  // row — the two triggers switch between them (mutually exclusive) and a form
  // never fully collapses. Defaults to the invite form.
  const [openPanel, setOpenPanel] = useState<'invite' | 'support'>('invite')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await budgetApi.getTenantUsage(tenantId))
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load members')
    } finally {
      setLoading(false)
    }
    // Separate and best-effort: a failure to list invites must not blank the
    // members table, which is the tab's primary content.
    try {
      setInvites((await budgetApi.getTenantInvites(tenantId)).invites)
    } catch {
      setInvites([])
    }
  }, [tenantId])

  useEffect(() => { load() }, [load])

  if (loading && !data) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
  if (error) return <div className="p-4 text-sm text-red-400">{error}</div>
  if (!data) return null

  return (
    <>
      <Section
        title={t('settings.platform.people.membersTitle', { defaultValue: 'Members' })}
        desc={t('settings.platform.people.membersDesc', {
          defaultValue: "Each member's spend this period, and the ceiling that applies to them. Only a platform administrator can hold a support seat — the control appears on every row, but the grant is refused for anyone else.",
        })}
      >
        {canEdit && (
          <div className="mb-2">
            {/* One form is ALWAYS visible below the buttons; the two triggers
                switch which (mutually exclusive), acting like tabs. The active
                trigger reads as pressed. */}
            <div className="flex justify-start gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setOpenPanel('invite')}
                aria-pressed={openPanel === 'invite'}
                className={`text-[11px] px-2 py-1 rounded ${openPanel === 'invite' ? 'themed-btn-primary' : 'themed-btn-secondary'}`}
              >
                {t('settings.platform.people.invite.open', { defaultValue: 'Invite member' })}
              </button>
              <button
                type="button"
                onClick={() => setOpenPanel('support')}
                aria-pressed={openPanel === 'support'}
                title={t('settings.platform.people.support.hint', {
                  defaultValue: 'Only a platform administrator can hold a support seat. Their AI usage here bills the platform, not the customer.',
                })}
                className={`text-[11px] px-2 py-1 rounded ${openPanel === 'support' ? 'themed-btn-primary' : 'themed-btn-secondary'}`}
              >
                {t('settings.platform.people.support.open', { defaultValue: 'Add support seat' })}
              </button>
            </div>
            {openPanel === 'invite'
              ? <InviteMemberPanel tenantId={tenantId} onDone={load} />
              : <AddSupportSeatPanel tenantId={tenantId} onDone={load} />}
          </div>
        )}
        <div className="themed-bg-secondary rounded-lg overflow-hidden">
          {/* "No data for this period" is the SPEND empty state and was wrong
              here: a console-provisioned tenant has no members at all until its
              owner accepts, which is the normal outcome rather than a gap in the
              figures. Say which of the two it is — and when there is an invite
              outstanding, say that, because it is the whole explanation. */}
          {data.users.length === 0 && (
            <div className="px-3 py-4 themed-text-muted text-sm">
              {invites.length > 0
                ? t('settings.platform.people.noMembersInvited', {
                    defaultValue: 'No members yet — the invite below has not been accepted.',
                  })
                : t('settings.platform.people.noMembers', {
                    defaultValue: 'No members yet — nobody has accepted an invite.',
                  })}
            </div>
          )}
          {/* Platform-support members float to the TOP — they are the rows an
              operator on THIS console acts on (role, seat, remove); the
              customer's own members are context below. Stable sort keeps each
              group's incoming order. */}
          {[...data.users]
            .sort((a, b) => Number(b.platformStaff ?? false) - Number(a.platformStaff ?? false))
            .map((u) => (
              <MemberRow key={u.email} row={u} tenantId={tenantId} canEdit={canEdit} onChanged={load} />
            ))}
        </div>
      </Section>

      {invites.length > 0 && (
        <Section
          title={t('settings.platform.people.invitesTitle', { defaultValue: 'Waiting to accept' })}
          desc={t('settings.platform.people.invitesDesc', {
            defaultValue:
              'Invited, but not in yet. A tenant provisioned from this console normally starts here — its owner has been asked in and has not accepted.',
          })}
        >
          <div className="themed-bg-secondary rounded-lg overflow-hidden">
            {invites.map((i) => (
              <InviteRow key={i.id} invite={i} tenantId={tenantId} canEdit={canEdit} onChanged={load} />
            ))}
          </div>
        </Section>
      )}

      {data.teams.length > 0 && (
        <Section
          title={t('settings.platform.people.teamsTitle', { defaultValue: 'Teams' })}
          desc={t('settings.platform.people.teamsDesc', {
            defaultValue: 'A team cap is a CONTRIBUTION to its members’ ceilings — 0 contributes nothing, it does not block.',
          })}
        >
          <div className="themed-bg-secondary rounded-lg overflow-hidden">
            {data.teams.map((tm) => (
              <div key={tm.id} className="px-3 py-2.5 border-b themed-border last:border-b-0 flex items-baseline justify-between gap-3">
                <span className="themed-text text-sm truncate">{tm.name}</span>
                <span className="text-xs themed-text-muted tabular-nums shrink-0">
                  {formatMoney(tm.used, moneyOpts)}
                  {' / '}
                  {formatMoney(tm.cap, moneyOpts)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  )
}

/**
 * One pending invite, with the two things an operator can do about it (5c).
 *
 * No spend column, because there is no spend — showing $0.00 beside a ceiling
 * would render someone who has never logged in as a member with a perfect
 * record. What it shows instead is HOW LONG they have been waiting, which is the
 * actionable fact: an invite sitting for a week means the email did not arrive.
 * Hence "Send again" right beside it.
 *
 * The console CREATES this state — provisioning a tenant invites the owner
 * rather than seating them — and until now could not resolve it. Both actions go
 * through PLATFORM-gated routes, not the org router's: those need membership in
 * this customer's org, which a platform admin does not have.
 *
 * There is no "copy link" on purpose. The invite URL embeds a bearer token for
 * joining the org; an endpoint that returned it would put a live credential in a
 * response body and, sooner or later, in a screenshot. Resend puts the link
 * where it already goes.
 */
function InviteRow({ invite, tenantId, canEdit, onChanged }: {
  invite: PendingInvite
  tenantId: string
  canEdit: boolean
  onChanged: () => void | Promise<void>
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()
  const [busy, setBusy] = useState<'resend' | 'revoke' | 'role' | null>(null)
  const days = Math.max(0, Math.floor((Date.now() - new Date(invite.createdAt).getTime()) / 86_400_000))

  // A pending invite's role is editable before it is accepted, so an operator
  // can correct it without withdrawing and re-inviting.
  const changeRole = async (role: string) => {
    setBusy('role')
    try {
      await budgetApi.setTenantInviteRole(tenantId, invite.id, role)
      toast.success(t('settings.platform.people.roleChange.done', { defaultValue: 'Role updated.' }))
      await onChanged()
    } catch (e: any) {
      toast.error(e?.message || t('common.error', { defaultValue: 'Something went wrong' }))
    } finally { setBusy(null) }
  }

  const resend = async () => {
    setBusy('resend')
    try {
      await budgetApi.resendTenantInvite(tenantId, invite.id)
      toast.success(t('settings.platform.people.resent', {
        defaultValue: 'Invite sent again to {{email}}.', email: invite.email,
      }))
    } catch (e: any) {
      toast.error(e?.message || 'Could not resend the invite.')
    } finally { setBusy(null) }
  }

  const revoke = async () => {
    const ok = await confirm({
      title: t('settings.platform.people.revokeTitle', { defaultValue: 'Withdraw this invitation?' }),
      // Says what it does AND what it does not: the address can be invited
      // again, so this is not a ban.
      message: t('settings.platform.people.revokeBody', {
        defaultValue:
          '{{email}} will no longer be able to join with the link they were sent. They can be invited again at any time.',
        email: invite.email,
      }),
      confirmText: t('settings.platform.people.revokeOk', { defaultValue: 'Withdraw' }),
    })
    if (!ok) return
    setBusy('revoke')
    try {
      await budgetApi.revokeTenantInvite(tenantId, invite.id)
      await onChanged()
      toast.success(t('settings.platform.people.revoked', { defaultValue: 'Invitation withdrawn.' }))
    } catch (e: any) {
      toast.error(e?.message || 'Could not withdraw the invite.')
    } finally { setBusy(null) }
  }

  return (
    <div className="px-3 py-2.5 border-b themed-border last:border-b-0 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <span className="themed-text text-sm truncate">{invite.email}</span>
        {canEdit ? (
          <select
            value={['owner', 'admin', 'member', 'viewer'].includes(invite.role) ? invite.role : 'member'}
            disabled={busy !== null}
            onChange={(e) => void changeRole(e.target.value)}
            className="ml-2 themed-input border rounded px-1 py-0.5 text-[11px] outline-none focus:border-blue-500 disabled:opacity-50 align-middle"
          >
            <option value="owner">{t('settings.platform.people.invite.roleOwner', { defaultValue: 'Owner' })}</option>
            <option value="admin">{t('settings.platform.people.invite.roleAdmin', { defaultValue: 'Admin' })}</option>
            <option value="member">{t('settings.platform.people.invite.roleMember', { defaultValue: 'Member' })}</option>
            <option value="viewer">{t('settings.platform.people.invite.roleViewer', { defaultValue: 'Viewer' })}</option>
          </select>
        ) : (
          <span className="themed-text-muted text-[11px] font-mono ml-2">{invite.role}</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="themed-text-muted text-xs">
          {days === 0
            ? t('settings.platform.people.invitedToday', { defaultValue: 'Invited today' })
            : t('settings.platform.people.invitedDaysAgo', {
                defaultValue: 'Invited {{n}}d ago',
                n: days,
              })}
        </span>
        {canEdit && (
          <>
            <button
              onClick={() => void resend()}
              disabled={busy !== null}
              className="text-[11px] px-2 py-1 rounded themed-btn-secondary disabled:opacity-50"
            >
              {busy === 'resend'
                ? t('common:saving')
                : t('settings.platform.people.resend', { defaultValue: 'Send again' })}
            </button>
            <button
              onClick={() => void revoke()}
              disabled={busy !== null}
              className="text-[11px] px-2 py-1 rounded themed-btn-secondary disabled:opacity-50"
            >
              {t('settings.platform.people.revoke', { defaultValue: 'Withdraw' })}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The "invite a billable member" form panel. One of this and the support-seat
 * panel is ALWAYS shown below the button row (the parent's `openPanel` picks
 * which) — switching between them unmounts and remounts, so each starts empty.
 * A platform admin inviting themselves uses this too — it is an ordinary member
 * invite; the separate support-seat panel is the non-billable path.
 *
 * The form stays put after a successful send (the panel is always visible), so
 * it clears its own fields to be ready for the next invite.
 */
function InviteMemberPanel({ tenantId, onDone }: {
  tenantId: string
  /** Called after a successful invite — the parent refreshes the member list. */
  onDone: () => void | Promise<void>
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('member')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!email.includes('@')) {
      toast.error(t('settings.platform.people.invite.badEmail', { defaultValue: 'Enter a valid email address.' }))
      return
    }
    setBusy(true)
    try {
      await budgetApi.inviteTenantMember(tenantId, { email: email.trim(), role })
      toast.success(t('settings.platform.people.invite.sent', {
        defaultValue: 'Invitation sent to {{email}}.', email: email.trim(),
      }))
      setEmail(''); setRole('member')   // ready for the next one — the panel stays open
      await onDone()
    } catch (e: any) {
      toast.error(e?.message || t('settings.platform.people.invite.failed', { defaultValue: 'Could not send the invitation.' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="mt-2 p-2 rounded themed-bg-secondary flex items-center gap-2 flex-wrap">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('settings.platform.people.invite.emailPlaceholder', { defaultValue: 'email@address' })}
        className="themed-input border rounded px-2 py-1 text-xs w-56 outline-none focus:border-blue-500"
      />
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
      >
        <option value="owner">{t('settings.platform.people.invite.roleOwner', { defaultValue: 'Owner' })}</option>
        <option value="member">{t('settings.platform.people.invite.roleMember', { defaultValue: 'Member' })}</option>
        <option value="admin">{t('settings.platform.people.invite.roleAdmin', { defaultValue: 'Admin' })}</option>
        <option value="viewer">{t('settings.platform.people.invite.roleViewer', { defaultValue: 'Viewer' })}</option>
      </select>
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy}
        className="text-[11px] px-2 py-1 rounded themed-btn-primary disabled:opacity-50"
      >
        {busy ? t('common:saving') : t('settings.platform.people.invite.send', { defaultValue: 'Send invite' })}
      </button>
    </div>
  )
}

/**
 * The "add a platform SUPPORT SEAT" form panel — a non-billable seat that only a
 * platform administrator may hold. One of this and the invite panel is always
 * shown below the button row (the parent picks which). The constraint is STATED,
 * not predicted: the row cannot know which addresses are platform staff without
 * disclosing it, so the server is the authority and its `NOT_PLATFORM_ADMIN`
 * refusal is surfaced. Clears its field after a successful add — the panel stays.
 */
function AddSupportSeatPanel({ tenantId, onDone }: {
  tenantId: string
  onDone: () => void | Promise<void>
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('member')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!email.includes('@')) {
      toast.error(t('settings.platform.people.support.badEmail', { defaultValue: 'Enter a valid email address.' }))
      return
    }
    setBusy(true)
    try {
      await budgetApi.addTenantSupportSeat(tenantId, { email: email.trim(), role })
      toast.success(t('settings.platform.people.support.added', {
        defaultValue: 'Support seat added — {{email}} bills the platform here, not the customer.', email: email.trim(),
      }))
      setEmail(''); setRole('member')   // ready for the next one — the panel stays open
      await onDone()
    } catch (e: any) {
      // The staff-only refusal is the expected error path, not a surprise.
      toast.error(e?.message || t('settings.platform.people.support.failed', { defaultValue: 'Could not add the support seat.' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="mt-2 p-2 rounded themed-bg-secondary flex items-center gap-2 flex-wrap">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t('settings.platform.people.support.emailPlaceholder', { defaultValue: 'platform-admin@address' })}
        className="themed-input border rounded px-2 py-1 text-xs w-56 outline-none focus:border-blue-500"
      />
      {/* The role the support member holds IN the tenant (defaults to Member).
          Reuses the invite panel's role labels — same set of roles. */}
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500"
      >
        <option value="owner">{t('settings.platform.people.invite.roleOwner', { defaultValue: 'Owner' })}</option>
        <option value="member">{t('settings.platform.people.invite.roleMember', { defaultValue: 'Member' })}</option>
        <option value="admin">{t('settings.platform.people.invite.roleAdmin', { defaultValue: 'Admin' })}</option>
        <option value="viewer">{t('settings.platform.people.invite.roleViewer', { defaultValue: 'Viewer' })}</option>
      </select>
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy}
        className="text-[11px] px-2 py-1 rounded themed-btn-primary disabled:opacity-50"
      >
        {busy ? t('common:saving') : t('settings.platform.people.support.add', { defaultValue: 'Add seat' })}
      </button>
    </div>
  )
}

function MemberRow({ row, tenantId, canEdit, onChanged }: {
  row: MemberUsageRow
  tenantId: string
  canEdit: boolean
  onChanged: () => void | Promise<void>
}) {
  const { t } = useTranslation('core-ui')
  const moneyOpts = { currency: useBillingCurrency() }
  const toast = useToast()
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)

  // A platform support seat means this person's AI spend in this tenant bills
  // the platform org, not the customer. Granting is refused server-side unless
  // the target is a platform administrator, so the error path is reachable and
  // must be shown rather than swallowed.
  const setSeat = async (enabled: boolean) => {
    if (!enabled) {
      const ok = await confirm({
        title: t('settings.platform.customer.people.seat.revokeTitle', {
          defaultValue: 'Revoke platform support seat?',
        }),
        message: t('settings.platform.customer.people.seat.revokeBody', {
          defaultValue:
            'New AI usage by {{email}} in this tenant will bill the customer again. Their access and role are unchanged.',
          email: row.email,
        }),
        confirmText: t('settings.platform.customer.people.seat.revokeConfirm', {
          defaultValue: 'Revoke seat',
        }),
      })
      if (!ok) return
    }
    setBusy(true)
    try {
      await budgetApi.setTenantMemberPlatformSeat(tenantId, row.email, enabled)
      toast.success(
        enabled
          ? t('settings.platform.customer.people.seat.granted', {
              defaultValue: 'Platform support seat granted — their usage here bills the platform.',
            })
          : t('settings.platform.customer.people.seat.revoked', {
              defaultValue: 'Platform support seat revoked.',
            }),
      )
      await onChanged()
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : t('common.error', { defaultValue: 'Something went wrong' }),
      )
    } finally {
      setBusy(false)
    }
  }
  // "Remove from tenant" is the inverse of "Add support seat": it removes the
  // membership entirely so a platform admin who supported this tenant doesn't
  // linger as a billable member. Shown ONLY on platform-staff rows
  // (`row.platformStaff`, a console-only flag) — the customer's OWN members are
  // not the console's to remove, so the button never appears on them (and the
  // DELETE route refuses them regardless). It DOES appear on a platform-staff
  // owner; the server removes them unless they are the last owner (last-owner
  // floor), returning `LAST_OWNER` — surfaced as a toast below.
  const removeMember = async () => {
    const ok = await confirm({
      title: t('settings.platform.people.remove.title', { defaultValue: 'Remove this member from the tenant?' }),
      message: t('settings.platform.people.remove.body', {
        defaultValue: '{{email}} will lose all access to this tenant. Only a platform support member can be removed here.',
        email: row.email,
      }),
      confirmText: t('settings.platform.people.remove.confirm', { defaultValue: 'Remove' }),
    })
    if (!ok) return
    setBusy(true)
    try {
      await budgetApi.removeTenantMember(tenantId, row.email)
      toast.success(t('settings.platform.people.remove.done', { defaultValue: 'Member removed from the tenant.' }))
      await onChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error', { defaultValue: 'Something went wrong' }))
    } finally { setBusy(false) }
  }

  // Change a PLATFORM-STAFF member's role (their own, or another staff member's)
  // in this tenant. Shown only on platform-staff rows — the customer's own
  // members' roles are managed from their organization, and the server refuses
  // them. The server also refuses demoting the last owner (`LAST_OWNER`).
  const changeRole = async (newRole: string) => {
    setBusy(true)
    try {
      await budgetApi.setTenantMemberRole(tenantId, row.email, newRole)
      toast.success(t('settings.platform.people.roleChange.done', { defaultValue: 'Role updated.' }))
      await onChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error', { defaultValue: 'Something went wrong' }))
    } finally { setBusy(false) }
  }

  const { roleCap, teamSum, adjust } = row.caps.components
  const cap = row.caps.userCap

  // `null` = unlimited, `0` = blocked. Opposites that both look falsy.
  const pct = cap != null && cap > 0 ? Math.min(100, (row.usage.total / cap) * 100) : null

  // `CAP_ROLES` is the canonical set the cap resolver knows. `noaccess` is
  // deliberately outside it and is a legitimate stored role, so it counts as
  // known here — the distinction being drawn is "someone configured this" vs
  // "the code has never heard of this", not "is it in CAP_ROLES".
  const knownRole = row.role === 'noaccess' || CAP_ROLES.some(r => r.id === row.role)

  return (
    <div
      className={`px-3 py-2.5 border-b themed-border last:border-b-0${
        // Platform-support members stay set apart by a left accent bar matching
        // the "Platform support" badge — no background fill — so the row the
        // operator manages reads as distinct without a heavy highlight.
        row.platformStaff ? ' border-l-2 border-l-sky-400/70' : ''
      }`}
    >
      {/* Line 1: identity on the left, spend on the right. NO wrap — the spend
          figure always stays right-aligned like every other row, regardless of
          how many controls a platform-support row carries (they go on line 2). */}
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
          <span className="themed-text text-sm truncate">{row.email}</span>
          {/* A platform-staff row's role is an editable dropdown right beside the
              email (server refuses the customer's own members and the last-owner
              demotion); the customer's own members show static role text. */}
          {canEdit && row.platformStaff ? (
            <select
              value={['owner', 'admin', 'member', 'viewer'].includes(row.role) ? row.role : 'member'}
              disabled={busy}
              onChange={(e) => void changeRole(e.target.value)}
              title={t('settings.platform.people.roleChange.hint', {
                defaultValue: 'Change this platform support member’s role in the tenant.',
              })}
              className="themed-input border rounded px-1 py-0.5 text-[11px] outline-none focus:border-blue-500 disabled:opacity-50"
            >
              <option value="owner">{t('settings.platform.people.invite.roleOwner', { defaultValue: 'Owner' })}</option>
              <option value="admin">{t('settings.platform.people.invite.roleAdmin', { defaultValue: 'Admin' })}</option>
              <option value="member">{t('settings.platform.people.invite.roleMember', { defaultValue: 'Member' })}</option>
              <option value="viewer">{t('settings.platform.people.invite.roleViewer', { defaultValue: 'Viewer' })}</option>
            </select>
          ) : (
            <span className="themed-text-muted text-[11px] font-mono">{row.role}</span>
          )}
          {row.platformSeat && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 align-middle"
              title={t('settings.platform.customer.people.seat.badgeHint', {
                defaultValue:
                  'Platform support seat — AI usage by this person in this tenant bills the platform org, not the customer.',
              })}
            >
              {t('settings.platform.customer.people.seat.badge', {
                defaultValue: 'Platform support',
              })}
            </span>
          )}
          {/* A MARKER that is no longer billing anyone — the person held a seat
              and has since left the platform team. Surfaced so an operator knows
              the row needs clearing; it re-arms if they are ever re-promoted. */}
          {(row.seatMarked ?? row.platformSeat) && !row.platformSeat && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 align-middle"
              title={t('settings.platform.customer.people.seat.staleHint', {
                defaultValue:
                  'This membership still carries a support-seat marker, but the person is no longer a platform administrator, so their usage bills this tenant. Revoke it — otherwise it starts billing the platform again if they are re-promoted.',
              })}
            >
              {t('settings.platform.customer.people.seat.stale', {
                defaultValue: 'Seat marker (inactive)',
              })}
            </span>
          )}
        </div>
        <span className="themed-text text-sm tabular-nums shrink-0">
          {formatMoney(row.usage.total, moneyOpts)}
          <span className="themed-text-muted">
            {' / '}
            {cap == null
              ? t('settings.budget.unlimited', { defaultValue: 'No ceiling' })
              : formatMoney(cap, moneyOpts)}
          </span>
        </span>
      </div>

      {/* Line 2: the interactive controls. Kept off line 1 so the spend figure
          above never gets pushed onto its own line. */}
      {canEdit && (
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            /* The MARKER, never the badge: a stale seat shows no badge and must
               still be revocable. `?? platformSeat` because the field is ABSENT on
               an older API during a rolling restart, and defaulting a live seat to
               "no marker" would offer to grant a seat that already exists. */
            onClick={() => setSeat(!(row.seatMarked ?? row.platformSeat))}
            disabled={busy}
            /* Always present, server-gated: the row cannot disclose WHETHER this
               member may hold a seat (that fact rides the shared payload the
               customer also reads), so the constraint is stated and the server
               refuses anyone else with NOT_PLATFORM_ADMIN. */
            title={t('settings.platform.customer.people.seat.constraintHint', {
              defaultValue: 'Only a platform administrator can hold a support seat. Granting one to anyone else is refused.',
            })}
            className="text-[11px] px-2 py-1 rounded themed-btn-secondary disabled:opacity-50"
          >
            {(row.seatMarked ?? row.platformSeat)
              ? t('settings.platform.customer.people.seat.revokeAction', {
                  defaultValue: 'Revoke support seat',
                })
              : t('settings.platform.customer.people.seat.grantAction', {
                  defaultValue: 'Mark as support seat',
                })}
          </button>
          {row.platformStaff && (
            <button
              type="button"
              onClick={() => void removeMember()}
              disabled={busy}
              title={t('settings.platform.people.remove.hint', {
                defaultValue: 'Removes this platform support member from the tenant. The customer’s own members are managed from their organization.',
              })}
              /* Destructive — styled red like the other danger surfaces in the
                 console (border-red-500/40 bg-red-500/10 text-red-300). */
              className="text-[11px] px-2 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20 disabled:opacity-50"
            >
              {t('settings.platform.people.remove.action', { defaultValue: 'Remove from tenant' })}
            </button>
          )}
        </div>
      )}

      {pct != null && (
        <div className="mt-1 h-1 w-full rounded-full themed-bg-tertiary overflow-hidden">
          <div
            className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-blue-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* The arithmetic, BEHIND A DISCLOSURE (F-10).
          ────────────────────────────────────────────────────────────────
          This used to render unconditionally, in monospace, on every row:
          `role ∞ + teams $0.00 + adjustment $0.00 = ∞`. Two problems. It is the
          cap resolver's internals shown as arithmetic on the row of a PERSON,
          which is not the question the People tab answers; and the total it
          computes is already stated two inches to the right, so the same fact
          appeared twice on one line in two different notations.

          The resolved ceiling stays where it was. The terms move here, opened
          only by someone actually asking "why that number?" — which is the one
          case the breakdown was ever for.

          `<details>` rather than React state on purpose: one row's disclosure is
          independent of every other's, it survives a re-render without a state
          array keyed by email, and it is keyboard-operable for free.

          Suppressed entirely when there is nothing to explain — a single
          non-zero term means the ceiling IS the role's, and a disclosure that
          opens to restate the number above it is noise. */}
      {(teamSum !== 0 || adjust !== 0) && (
        <details className="mt-1 group">
          <summary className="text-[11px] themed-text-muted cursor-pointer list-none hover:themed-text transition-colors">
            <span aria-hidden className="inline-block w-3 transition-transform group-open:rotate-90">›</span>
            {t('settings.platform.people.capWhy', { defaultValue: 'How this ceiling is made up' })}
          </summary>
          {/* Words, not a formula. `roleCap` null means the role itself is
              unlimited, in which case the terms explain WHY the total is
              unlimited rather than adding up to a number — so the unlimited case
              gets its own sentence instead of an ∞ in a sum. */}
          <div className="mt-1 ml-3 text-[11px] themed-text-secondary space-y-0.5">
            <div>
              {roleCap == null
                ? t('settings.platform.people.capRoleUnlimited', {
                    defaultValue: 'Their role “{{role}}” sets no ceiling, so nothing below can impose one.',
                    role: row.role,
                  })
                : t('settings.platform.people.capRoleIs', {
                    defaultValue: 'Their role “{{role}}” allows {{amount}}.',
                    role: row.role, amount: formatMoney(roleCap, moneyOpts),
                  })}
            </div>
            {teamSum !== 0 && (
              <div>
                {t('settings.platform.people.capTeamsAdd', {
                  defaultValue: 'Team membership adds {{amount}}.',
                  amount: formatMoney(teamSum, moneyOpts),
                })}
              </div>
            )}
            {adjust !== 0 && (
              <div>
                {t('settings.platform.people.capAdjustAdd', {
                  defaultValue: 'A per-person adjustment adds {{amount}}.',
                  amount: formatMoney(adjust, moneyOpts),
                })}
              </div>
            )}
          </div>
        </details>
      )}

      {/* A zero ceiling has two very different causes and they must not be
          conflated. `guest`/`noaccess` resolve to 0 BY DESIGN — that is the
          documented default and nothing is wrong. An UNRECOGNISED role also
          resolves to 0, silently, as a fail-closed fallback — and that one is a
          misconfiguration nobody would otherwise see. Saying "this might be an
          unknown role" on a guest row would cry wolf on the common case and
          train the reader to ignore the warning that matters. */}
      {cap === 0 && (
        <div className={`mt-1 text-[11px] ${knownRole ? 'themed-text-muted' : 'text-amber-300'}`}>
          {knownRole
            ? t('settings.platform.people.blockedByRole', {
                defaultValue: 'Cannot spend — the default for this role.',
              })
            : t('settings.platform.people.blockedUnknownRole', {
                defaultValue:
                  'Cannot spend because the system does not recognise the role “{{role}}”. Unknown roles fail closed at 0.',
                role: row.role,
              })}
        </div>
      )}
    </div>
  )
}
